import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useFeedbackContext } from '@/contexts/FeedbackContext'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { ProjectCreateOrEditPendingComponent } from '@/features/admin/projects/create-edit/components/ProjectCreateOrEditPendingComponent'
import { ProjectForm } from '@/features/admin/projects/create-edit/components/ProjectForm'
import { projectFormOptions } from '@/features/admin/projects/create-edit/helper/projectFormOptions'
import { useCreateProject } from '@/features/admin/projects/create-edit/mutations/useCreateProject'
import { useProjectCreateOrEditSchema } from '@/features/admin/projects/create-edit/schemas/projectCreateOrEditSchema'
import {
    trackingKindsQueryOptions,
    useTrackingKinds,
} from '@/features/edit-trackings/queries/useTrackingKinds'
import { DeletedItemSelection } from '@/generated/Enumerations/DeletedItemSelection_pb'
import { IdentityUserSortBy } from '@/generated/Enumerations/IdentityUserSortBy_pb'
import { IdentityUserFiltersDto } from '@/generated/Messages/Filters_pb'
import { useAppForm } from '@/hooks/useAppForm'
import { usersQueryOptions, useUsers } from '@/queries/useUsers'
import { OmitDeepMessageFields } from '@/types/OmitDeepMessageFields'

const seleniumPrefix = 'create-project'

const RouteComponent: React.FC = () => {
    const {
        data: { users },
    } = useUsers(usersFilters)
    const {
        data: { trackingKinds },
    } = useTrackingKinds()
    const { t } = useTranslation()
    const projectCreateOrEditSchema = useProjectCreateOrEditSchema()

    const { addFeedback } = useFeedbackContext()
    const createProject = useCreateProject()
    const form = useAppForm({
        ...projectFormOptions(),

        validators: {
            onSubmit: projectCreateOrEditSchema,
        },

        onSubmitInvalid: () => {
            addFeedback?.({
                variant: 'snackbar',
                severity: 'warning',
                title: t('snackbar.failedValidation'),
            })
        },

        onSubmit: async ({ value, formApi }) => {
            await createProject.mutateAsync(value)

            formApi.reset()
        },
    })

    return (
        <>
            <title>{t('projects.urlCreate')}</title>

            <form.AppForm>
                <form.AdminForm
                    title={t('projects.create')}
                    backTo={'/admin/projects'}
                >
                    <form.Blocker data-selenium={`${seleniumPrefix}-blocker`} />

                    <ProjectForm
                        form={form}
                        trackingKinds={trackingKinds}
                        users={users}
                    />
                </form.AdminForm>
            </form.AppForm>
        </>
    )
}
const PendingComponent: React.FC = () => {
    const { t } = useTranslation()
    return <ProjectCreateOrEditPendingComponent title={t('projects.edit')} />
}

const usersFilters: Partial<OmitDeepMessageFields<IdentityUserFiltersDto>> = {
    deletedItemSelection: DeletedItemSelection.NORMAL_ONLY,
    sorting: [{ sortBy: IdentityUserSortBy.LAST_NAME }],
}

export const Route = createFileRoute('/__base-layout/admin/projects/create')({
    component: RouteComponent,
    beforeLoad: async ({ context: { user, queryClient, grpcClients } }) => {
        assertUserIsAdminOrRedirect(user)
        await Promise.all([
            queryClient.ensureQueryData(trackingKindsQueryOptions(grpcClients)),
            queryClient.ensureQueryData(
                usersQueryOptions(grpcClients, usersFilters)
            ),
        ])
    },
    pendingComponent: PendingComponent,
})
